import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { DTypeCol } from '@grupo-constant/next-dtype/types';
import { MasterData } from '../../master-data/master-data.types';

class DualListboxItem<T = any> {
    constructor(
        public id: string,
        public state: {
            selected: boolean;
            added: boolean;
            index: number;
        } = {
                selected: false,
                added: false,
                index: null,
            },
        public data?: T,
    ) { }
}

class DualListbox<T = any> {

    /** The column where all the selected items are */
    selectedColumn: 'available' | 'added' = null;

    /** The initial item of a shift selection */
    private initialItem: DualListboxItem<T> = null;
    /** Last selection action, if true the last action selected an item, else the action deselected it */
    private lastActionIsSelect = false;

    constructor(
        /** The items for the lists, each has a state of where it is and could contain data */
        public items: DualListboxItem<T>[],
    ) {
        const ids = items.map(item => item.id);
        if ((new Set(ids).size !== ids.length)) {
            throw new Error('The item ids must be unique');
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // Getters and setters
    // -----------------------------------------------------------------------------------------------------

    /** Items in the 'added items' column sorted by item index */
    get addedItems() {
        return [...this.items] //copy of items array so the sort doesn't alter the original
            .filter(i => i.state.added) //filter by added
            .sort((a, b) => a.state.index - b.state.index); //sort by index
    }

    /** Items in the 'available items' column */
    get notAddedItems() {
        return this.items.filter(i => !i.state.added);
    }

    /** Currently selected items */
    get selectedItems() {
        return this.items.filter(i => i.state.selected);
    }


    // -----------------------------------------------------------------------------------------------------
    // Public Methods
    // -----------------------------------------------------------------------------------------------------

    /** Deselects all items */
    public deselectAll() {
        this.items.forEach(item => item.state.selected = false);
    }

    /** Removes all items from the 'added items' column */
    public removeAllFromAdded() {
        this.items.forEach(item => item.state.added = false);
    }

    /** Deselects and removes from the 'added items' column all items */
    public resetStatus() {
        this.deselectAll();
        this.removeAllFromAdded();
    }

    /**
     * Changes the selection of an item or a range of items.
     *
     * If key is pressed (control or shift) it will select an item, deselecting all others.
     *
     * If shift is pressed and no previous item was
     * selected it selects it, if not all items between the last selected item and this one inclueded will
     * be selected.
     *
     * If control is pressed the selection will not remove the last selection. If the item selected was selected
     * before it will remove the selection.
     *
     * If the last non shift selection deselected an item and control is pressed a shift selection will deselect
     * the range of items.
     *
     * Regardless of keys pressed when an item is selected all items from the other column will be deselected.
     *
     * @param itemToSelect Item or last item of a range to be selected or deselected
     * @param ctrl If control key is pressed
     * @param shift If shift key is pressed
     */
    public select(itemToSelect: DualListboxItem, ctrl = false, shift = false) {

        const columnIsAdded = itemToSelect.state.added;

        //deselect items of the other column
        (itemToSelect.state.added ? this.notAddedItems : this.addedItems).forEach(item => item.state.selected = false);

        //if shift was pressed
        if (shift) {

            //if no initial item or the item is in another column
            if (!this.initialItem || this.initialItem.state.added !== itemToSelect.state.added) {

                //select item, set it as initial item, and set last action as select
                itemToSelect.state.selected = true;
                this.initialItem = itemToSelect;
                this.lastActionIsSelect = true;

                //set selected column
                this.selectedColumn = itemToSelect.state.added ? 'added' : 'available';
                return;
            }

            //get indexes of first and last item to select
            const itemsForIndex = columnIsAdded ? this.addedItems : this.notAddedItems;
            const [indexA, indexB] = (columnIsAdded ? [
                itemsForIndex.find(item => this.initialItem === item).state.index,
                itemsForIndex.find(item => itemToSelect === item).state.index
            ] : [
                itemsForIndex.findIndex(item => this.initialItem === item),
                itemsForIndex.findIndex(item => itemToSelect === item)
            ]).sort();

            //get items to select
            const items = itemsForIndex.filter((_, i) => ((i >= indexA) && (i <= indexB)));

            //deselect other items if no ctrl was pressed
            if (!ctrl) { this.deselectAll(); }

            //check if should select or deselect and save the action
            const shouldSelect = (!ctrl || this.lastActionIsSelect);
            this.lastActionIsSelect = shouldSelect;

            //change selection of items
            items.forEach(item => item.state.selected = shouldSelect);

        } else { //if shift was not pressed

            //deselect other items if no ctrl was pressed
            if (!ctrl) { this.deselectAll(); }

            //change item selection and set as initial item for shift selection
            itemToSelect.state.selected = !itemToSelect.state.selected;
            this.initialItem = itemToSelect;

            //set last action
            this.lastActionIsSelect = itemToSelect.state.selected;
        }

        //set selected column
        if (this.items.every(item => !(item.state.selected))) { //if no item is selected unset selected column
            this.selectedColumn = null;
        } else if (itemToSelect.state.added) {
            this.selectedColumn = 'added';
        } else {
            this.selectedColumn = 'available';
        }
    }

    /** Sets the added state of selected items as true */
    public addItems() {
        //get length of added items to offset the order
        const offset = this.addedItems.length;
        this.selectedItems.forEach((item, i) => {
            item.state.added = true;
            item.state.index = offset + i;
        });
        this.selectedColumn = 'added';
    }

    /** Sets the added state of selected items as false */
    public removeItems() {
        this.selectedItems.forEach(item => item.state.added = false);
        let order = 0;
        this.items.forEach((item) => {
            if (item.state.added) {
                item.state.index = order;
                order++;
            } else {
                item.state.index = null;
            }
        });
        this.selectedColumn = 'available';
    }

    /**
     * Changes the index of the item to the requested value and changes the indexes of all the added items
     * to be sequential. The index of non added items will be null
     *
     * @param previousIndex The old index of the item
     * @param currentIndex The new index of the item
     */
    public moveItem(previousIndex: number, currentIndex: number) {

        /** direction of the moved item, for a positive number the direction is forwards */
        const direction = currentIndex - previousIndex;
        //exit if the direction is unchanged
        if (direction === 0) { return; }
        const items = this.addedItems;
        const movedForward = direction > 0;

        //assigns the requested new index to the item
        items[previousIndex].state.index = currentIndex;

        //changes indices of items moved by the index change of the item
        if (movedForward) {
            items.slice(previousIndex + 1, currentIndex + 1)
                .forEach(item => item.state.index--);
        } else {
            items.slice(currentIndex, previousIndex)
                .forEach(item => item.state.index++);
        }
    }
}

@Component({
    selector: 'dtype-list-config',
    templateUrl: 'dtype-list-config.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DtypeListConfigComponent implements OnInit {

    @Input() dtype: MasterData;
    @Input() readonly = false;
    @Output() changed = new EventEmitter<void>();

    selectorItems: DualListbox<DTypeCol>;

    constructor() { }

    /** Column names in the datatype */
    private get columnNames(): string[] {
        return this.dtype.columnsForList;
    }
    private set columnNames(names: string[]) {
        this.dtype.columnsForList = names;
    }

    /** Column names in the added column */
    private get selectorColumnNames(): string[] {
        return this.selectorItems.addedItems.map(item => item.id);
    }
    private set selectorColumnNames(names: string[]) {
        this.selectorItems.resetStatus();
        const items = this.selectorItems.items;
        names.forEach((name, i) => {
            const item = items.find(o => o.id === name);
            item.state.added = true;
            item.state.index = i;
        });
    }

    ngOnInit() {
        const items = this.dtype.availableColumnsForList.map(column => new DualListboxItem(column));
        this.selectorItems = new DualListbox(items);
        this.selectorColumnNames = this.columnNames;
    }

    /**
     * Moves the selected items to the specified list
     *
     * @param list Name of the list where the items should be moved
     */
    moveTo(list: 'available' | 'added') {
        switch (list) {
            case 'available':
                this.selectorItems.removeItems();
                break;

            case 'added':
                this.selectorItems.addItems();
                break;

            default:
                break;
        }
        this.changed.emit();
        this.columnNames = this.selectorColumnNames;
    }

    /**
     * Select an item or group of items
     *
     * @param event Click event
     * @param itemToSelect Item to be selected
     */
    select(event: MouseEvent, itemToSelect: DualListboxItem<DTypeCol>) {
        this.selectorItems.select(itemToSelect, event.ctrlKey, event.shiftKey);
    }

    /**
     * Moves item to where it was dropped
     *
     * @param event Drag drop event
     */
    drop(event: CdkDragDrop<DualListboxItem<DTypeCol>[]>) {
        this.selectorItems.moveItem(event.previousIndex, event.currentIndex);
        this.columnNames = this.selectorColumnNames;
        this.changed.emit();
    }

}
